import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, CircleAlert, ClipboardCopy, CloudUpload, Copy, FileUp, Globe, HeartPulse, Image as ImageIcon,
  Loader2, Mail, Plus, RefreshCw, RotateCcw, Search, ShieldCheck, Sparkles, Trash2, Undo2, UserCog, Workflow, X,
} from 'lucide-react';
import type { HostedDomain, Migration, MigrationRecord, User } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { ago } from '@/lib/format';
import { useMe } from '@/lib/queries';
import { Avatar, Favicon } from './Avatar';
import { confirmDialog } from './Dialogs';
import { Button, Chip, cn, Empty, IconButton, Input, Modal, Textarea, tint } from './ui';

const STEPS = ['Domain', 'Records', 'Copy & verify', 'Nameservers', 'Email', 'Monitor'] as const;

function stepFor(m: Migration): number {
  switch (m.status) {
    case 'inspected': return 1;
    case 'records': return 1;
    case 'copied':
    case 'verified': return m.verify?.ready ? 3 : 2;
    case 'delegated': return 4;
    case 'email':
    case 'monitoring':
    case 'done': return 5;
    default: return 0;
  }
}

const PROVIDERS: Record<string, { name: string; steps: string[] }> = {
  godaddy: {
    name: 'GoDaddy',
    steps: ['Sign in to GoDaddy and open My Products → Domains.', 'Select the domain, then DNS (or "Manage DNS").', 'Choose the ⋯ menu / Action → Export Zone File (Windows: "Export as Text").', 'Open the downloaded file and drop it here, or paste its text.'],
  },
  azure: {
    name: 'Azure DNS',
    steps: ['Open portal.azure.com and launch Cloud Shell (the >_ icon at the top).', 'Paste the command below and press Enter. It finds the domain in whichever subscription holds it and prints the zone file.', 'Copy all of the output and paste it here.'],
  },
  route53: {
    name: 'Amazon Route 53',
    steps: ['Open the Route 53 console → Hosted zones → your domain.', 'Use "Export zone file" (Actions menu) if available, or select all records in the table and copy them.', 'Paste the result here — tables copied from the console work too.'],
  },
  google: {
    name: 'Google Cloud DNS',
    steps: ['In Cloud Shell run:  gcloud dns record-sets export zone.txt --zone=<zone-name> --zone-file-format', 'Open zone.txt and paste its contents here.'],
  },
  cloudflare: {
    name: 'Cloudflare (another account)',
    steps: ['In that Cloudflare account open the domain → DNS → Records.', 'Click Import and Export → Export DNS records.', 'Drop the downloaded file here.'],
  },
  namecheap: {
    name: 'Namecheap',
    steps: ['Domain List → Manage → Advanced DNS.', 'Namecheap has no export: select the host records table, copy it, and paste it here (or take a screenshot and drop it here).'],
  },
  squarespace: { name: 'Squarespace', steps: ['Domains → your domain → DNS → DNS Settings.', 'Copy the records table or take a screenshot of it and drop it here.'] },
  wix: { name: 'Wix', steps: ['Domains → ⋯ → Manage DNS Records.', 'Copy the records or take screenshots and drop them here.'] },
  other: { name: 'your DNS provider', steps: ['Look for "Export zone file" or "Export DNS records" in your provider\'s DNS settings.', 'If there is no export, copy the records table or take a screenshot — AI will read it.'] },
};

const CATEGORY_COLORS: Record<string, string> = { web: '#38bdf8', api: '#a78bfa', verification: '#94a3b8', email: '#f43f5e', delegation: '#f59e0b', other: '#64748b' };
const MAIL_NAMES: Record<string, string> = { icloud: 'iCloud', google: 'Google Workspace', microsoft365: 'Microsoft 365', zoho: 'Zoho', godaddy: 'GoDaddy Email', proton: 'Proton', fastmail: 'Fastmail', cloudflare: 'Cloudflare', none: 'no email', other: 'another provider' };

function rel(name: string, domain: string) {
  return name === domain ? '@' : name.endsWith(`.${domain}`) ? name.slice(0, -(domain.length + 1)) : name;
}

// ---------------------------------------------------------------------------

export function DomainsPanel() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['migrations'], queryFn: api.migrations });
  const [open, setOpen] = useState<string | 'new' | null>(null);
  useEffect(() => window.dts.events.onEvent((e) => e.type.startsWith('migration.') && qc.invalidateQueries({ queryKey: ['migrations'] })), [qc]);

  if (open) return <Wizard domain={open === 'new' ? null : open} onClose={() => (setOpen(null), qc.invalidateQueries({ queryKey: ['migrations'] }))} />;

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="text-[16px] font-semibold">Domains</h2>
          <p className="mt-0.5 max-w-xl text-[12.5px] text-muted">Move a domain's DNS to Cloudflare and its email to DTS Mail — your websites and APIs keep working, every record is verified before anything switches, and email changes roll back automatically if a test fails.</p>
        </div>
        <Button variant="ai" icon={<CloudUpload className="size-4" />} onClick={() => setOpen('new')}>Migrate a domain</Button>
      </div>
      <HostedDomains />
      <h3 className="mb-2 text-[13px] font-semibold text-muted">Migrations</h3>
      {list.isLoading ? null : !list.data?.length ? (
        <div className="glass rounded-2xl"><Empty icon={<Globe />} title="No domains migrated yet" hint="Start with a low-traffic domain. You can migrate one at a time." /></div>
      ) : (
        <div className="space-y-2">
          {list.data.map((m) => (
            <button key={m.domain} onClick={() => setOpen(m.domain)} className="glass flex w-full items-center gap-4 rounded-2xl px-4 py-3 text-left transition hover:border-line-strong">
              <Favicon domain={m.domain} size={22} />
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold">{m.domain}</div>
                <div className="text-[12px] text-muted">{STEPS[stepFor(m)]} · updated {ago(m.updatedUtc)}</div>
              </div>
              <StatusBadge m={m} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Hosted domains and their domain admins — people who can edit the domain's brand template and receive mail for its unclaimed addresses. System administrators only. */
function HostedDomains() {
  const qc = useQueryClient();
  const me = useMe().data;
  const isAdmin = me?.user.role === 'admin';
  const domains = useQuery({ queryKey: ['admin-domains'], queryFn: api.adminDomains, enabled: isAdmin });
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers, enabled: isAdmin });
  const [picking, setPicking] = useState<HostedDomain | null>(null);
  const byId = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u])), [users.data]);

  if (!isAdmin || !domains.data?.length) return null;

  const save = async (d: HostedDomain, ids: string[]) => {
    try {
      await api.setDomainAdmins(d.name, ids);
      qc.invalidateQueries({ queryKey: ['admin-domains'] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-[13px] font-semibold text-muted">Hosted domains</h3>
      <p className="mb-2 text-[12px] text-faint">Domain admins can update the brand template for their domains in Settings → Brand templates, and they get a copy of mail sent to any address on the domain that nobody owns (e.g. junk@).</p>
      <div className="space-y-2">
        {domains.data.map((d) => (
          <div key={d.name} className="glass flex items-start gap-4 rounded-2xl px-4 py-3">
            <Favicon domain={d.name} size={22} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold">{d.name}</span>
                {!d.enabled && <Chip color="#f43f5e">disabled</Chip>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {(d.adminUserIds ?? []).length === 0 && <span className="text-[12px] text-faint">No domain admins</span>}
                {(d.adminUserIds ?? []).map((id) => {
                  const u = byId.get(id);
                  const name = u ? u.displayName || u.email : 'Unknown user';
                  return (
                    <span key={id} className="group inline-flex items-center gap-1.5 rounded-full border border-line bg-bg/40 py-0.5 pl-0.5 pr-1 text-[12px]">
                      <Avatar name={name} address={u?.email ?? ''} size={18} />
                      {name}
                      <button className="rounded-full px-1 text-faint opacity-0 transition hover:text-danger group-hover:opacity-100" title="Remove domain admin" onClick={async () => {
                        if (!(await confirmDialog(`Remove ${name} as an admin of ${d.name}?`, "They will no longer be able to change this domain's brand template.", { confirmLabel: 'Remove', danger: true }))) return;
                        await save(d, d.adminUserIds.filter((x) => x !== id));
                      }}><X className="size-3" /></button>
                    </span>
                  );
                })}
              </div>
            </div>
            <Button size="sm" variant="ghost" icon={<UserCog className="size-3.5" />} onClick={() => setPicking(d)}>Add admins</Button>
          </div>
        ))}
      </div>
      {picking && (
        <PickAdmins
          domain={picking}
          users={users.data ?? []}
          onClose={() => setPicking(null)}
          onSave={async (ids) => {
            if (!(await save(picking, [...(picking.adminUserIds ?? []), ...ids]))) return;
            toast.success(`Updated admins for ${picking.name}`);
            setPicking(null);
          }}
        />
      )}
    </div>
  );
}

function PickAdmins({ domain, users, onClose, onSave }: { domain: HostedDomain; users: User[]; onClose: () => void; onSave: (ids: string[]) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const term = q.trim().toLowerCase();
  const candidates = users
    .filter((u) => !(domain.adminUserIds ?? []).includes(u.id) && !u.disabled)
    .filter((u) => !term || `${u.displayName} ${u.email}`.toLowerCase().includes(term));
  const toggle = (id: string) => setSel((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  return (
    <Modal open onClose={onClose} title={`Add admins for ${domain.name}`} width={480}>
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" />
      <div className="mt-3 max-h-[340px] space-y-1 overflow-y-auto">
        {candidates.length === 0 && <div className="py-6 text-center text-[12.5px] text-muted">No one else to add.</div>}
        {candidates.map((u) => (
          <label key={u.id} className={cn('flex cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2 transition', sel.has(u.id) ? 'bg-accent/15' : 'hover:bg-line')}>
            <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} className="size-4" />
            <Avatar name={u.displayName} address={u.email} size={28} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">
                {u.displayName}
                {u.role === 'admin' && <span className="ml-1.5 text-[11px] text-faint">system admin</span>}
              </div>
              <div className="truncate text-[12px] text-muted">{u.email}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={sel.size === 0} loading={busy} onClick={async () => {
          setBusy(true);
          try {
            await onSave([...sel]);
          } finally {
            setBusy(false);
          }
        }}>
          {sel.size > 1 ? `Add ${sel.size} admins` : 'Add admin'}
        </Button>
      </div>
    </Modal>
  );
}

function StatusBadge({ m }: { m: Migration }) {
  const map: Record<string, [string, string]> = {
    inspected: ['Started', '#94a3b8'], records: ['Reviewing records', '#38bdf8'], copied: ['Copied', '#a78bfa'], verified: ['Ready to switch', '#10b981'],
    delegated: ['DNS on Cloudflare', '#10b981'], email: ['Testing email', '#f59e0b'], monitoring: ['Monitoring', '#10b981'], done: ['Complete', '#10b981'],
  };
  const [label, color] = map[m.status] ?? ['?', '#94a3b8'];
  return <span className="pill badge rounded-full px-2.5 py-1 text-[11.5px] font-semibold" style={{ background: tint(color), color }}>{label}</span>;
}

// ---------------------------------------------------------------------------

function Wizard({ domain: initial, onClose }: { domain: string | null; onClose: () => void }) {
  const [domain, setDomain] = useState(initial);
  const q = useQuery({ queryKey: ['migration', domain], queryFn: () => api.migration(domain!), enabled: !!domain });
  const qc = useQueryClient();
  const m = q.data;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (m) setStep(stepFor(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.status, m?.verify?.ready]);

  useEffect(() => window.dts.events.onEvent((e) => e.type.startsWith('migration.') && e.data?.domain === domain && qc.invalidateQueries({ queryKey: ['migration', domain] })), [qc, domain]);

  const run = async (label: string, fn: () => Promise<Migration>, success?: string) => {
    setBusy(label);
    try {
      const next = await fn();
      qc.setQueryData(['migration', next.domain], next);
      if (!domain) setDomain(next.domain);
      if (success) toast.success(success);
      return next;
    } catch (e) {
      toast.error(errorMessage(e), { duration: 10000 });
      return null;
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <IconButton label="Back to domains" onClick={onClose}><ArrowLeft /></IconButton>
        {domain && <Favicon domain={domain} size={22} />}
        <h2 className="flex-1 text-[18px] font-semibold">{domain ? `Migrate ${domain}` : 'Migrate a domain'}</h2>
        {m && (
          <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={async () => {
            if (!(await confirmDialog(`Forget the ${m.domain} migration?`, 'This only removes the wizard progress. Nothing in Cloudflare or at your DNS provider is changed.', { confirmLabel: 'Forget', danger: true }))) return;
            await api.deleteMigration(m.domain);
            onClose();
          }}>Forget</Button>
        )}
      </div>

      {/* stepper */}
      <div className="mb-6 flex items-center gap-1">
        {STEPS.map((s, i) => {
          const reached = m ? i <= stepFor(m) : i === 0;
          return (
            <button key={s} disabled={!reached} onClick={() => setStep(i)} className={cn('flex flex-1 items-center gap-2 rounded-xl px-2.5 py-2 text-left transition', step === i ? 'bg-accent/15' : reached ? 'hover:bg-line' : 'opacity-40')}>
              <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold', m && i < stepFor(m) ? 'bg-ok text-white' : step === i ? 'bg-accent text-accent-fg' : 'bg-line text-muted')}>
                {m && i < stepFor(m) ? <Check className="size-3.5" /> : i + 1}
              </span>
              <span className={cn('truncate text-[12.5px] font-medium', step === i ? 'text-fg' : 'text-muted')}>{s}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.15 }}>
          {step === 0 && <StepDomain m={m} busy={busy} onInspect={(d) => run('inspect', () => api.inspectDomain(d)).then((r) => r && setStep(r.status === 'delegated' ? 4 : 1))} />}
          {step === 1 && m && <StepRecords m={m} busy={busy} run={run} onNext={() => setStep(2)} />}
          {step === 2 && m && <StepCopy m={m} busy={busy} run={run} onNext={() => setStep(3)} />}
          {step === 3 && m && <StepNameservers m={m} busy={busy} run={run} onNext={() => setStep(4)} />}
          {step === 4 && m && <StepEmail m={m} busy={busy} run={run} onNext={() => setStep(5)} />}
          {step === 5 && m && <StepMonitor m={m} busy={busy} run={run} />}
        </motion.div>
      </AnimatePresence>

      {m && m.log.length > 0 && (
        <details className="mt-8 rounded-2xl border border-line bg-bg/30 px-4 py-3">
          <summary className="cursor-pointer text-[12.5px] font-medium text-muted">Activity log ({m.log.length})</summary>
          <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
            {[...m.log].reverse().map((l, i) => (
              <div key={i} className={cn('flex gap-3 text-[12px]', l.level === 'error' ? 'text-danger' : l.level === 'warn' ? 'text-warn' : 'text-muted')}>
                <span className="w-28 shrink-0 tabular-nums text-faint">{new Date(l.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                <span className="selectable">{l.text}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

type Run = (label: string, fn: () => Promise<Migration>, success?: string) => Promise<Migration | null>;

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('glass rounded-2xl p-5', className)}>{children}</div>;
}

function Fact({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' }) {
  return (
    <div className="rounded-xl bg-bg/40 px-3.5 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-faint">{label}</div>
      <div className={cn('mt-0.5 text-[13.5px] font-medium', tone === 'ok' && 'text-ok', tone === 'warn' && 'text-warn', tone === 'danger' && 'text-danger')}>{value}</div>
    </div>
  );
}

// ---------- step 1: domain ----------

function StepDomain({ m, busy, onInspect }: { m?: Migration; busy: string | null; onInspect: (d: string) => void }) {
  const [d, setD] = useState(m?.domain ?? '');
  return (
    <div className="space-y-4">
      <Card>
        <div className="text-[14px] font-semibold">Which domain do you want to move?</div>
        <p className="mt-1 text-[12.5px] text-muted">We look up where it is registered, where its DNS is hosted today and who handles its email. Nothing changes yet.</p>
        <form className="mt-4 flex gap-2" onSubmit={(e) => (e.preventDefault(), d.trim() && onInspect(d.trim()))}>
          <Input autoFocus value={d} onChange={(e) => setD(e.target.value)} placeholder="example.com" className="h-11 text-[15px]" />
          <Button type="submit" variant="primary" size="lg" loading={busy === 'inspect'} icon={<Search className="size-4" />}>Look up</Button>
        </form>
      </Card>
      {m && <InspectSummary m={m} />}
    </div>
  );
}

function InspectSummary({ m }: { m: Migration }) {
  return (
    <Card>
      <div className="grid grid-cols-4 gap-2.5">
        <Fact label="Registrar" value={m.registrar ?? 'Unknown'} />
        <Fact label="DNS hosted at" value={PROVIDERS[m.provider]?.name ?? m.provider} />
        <Fact label="Email handled by" value={MAIL_NAMES[m.mailProvider] ?? m.mailProvider} />
        <Fact label="DNSSEC" value={m.dnssec ? 'On — must be turned off first' : 'Off'} tone={m.dnssec ? 'danger' : 'ok'} />
      </div>
      {m.dnssec && (
        <div className="mt-3 flex gap-2 rounded-xl bg-danger/10 px-3 py-2 text-[12.5px] text-danger">
          <CircleAlert className="mt-0.5 size-4 shrink-0" />
          DNSSEC is on. Turn it off at {m.registrar ?? 'your registrar'} (remove the DS record), wait an hour, then look the domain up again. Moving with DNSSEC on would take the domain offline.
        </div>
      )}
      {m.alreadyOnCloudflare && (
        <div className="mt-3 flex gap-2 rounded-xl bg-ok/10 px-3 py-2 text-[12.5px] text-ok"><CheckCircle2 className="mt-0.5 size-4 shrink-0" />This domain's DNS is already on Cloudflare — skip straight to switching email.</div>
      )}
      <div className="mt-3 text-[12px] text-faint">Current nameservers: {m.oldNameservers.join(', ')}</div>
    </Card>
  );
}

// ---------- step 2: records ----------

function azureExportCommand(domain: string, shell: 'powershell' | 'bash'): string {
  return shell === 'powershell'
    ? `$d='${domain}'; foreach ($s in (az account list --query "[].id" -o tsv)) { $rg = az network dns zone list --subscription $s --query "[?name=='$d'].resourceGroup" -o tsv | Select-Object -First 1; if ($rg) { az network dns zone export --subscription $s -g $rg -n $d; break } }`
    : `d=${domain}; for s in $(az account list --query "[].id" -o tsv); do rg=$(az network dns zone list --subscription $s --query "[?name=='$d'].resourceGroup" -o tsv | head -1); if [ -n "$rg" ]; then az network dns zone export --subscription $s -g $rg -n $d; break; fi; done`;
}

function AzureExportCommand({ domain }: { domain: string }) {
  const [shell, setShell] = useState<'powershell' | 'bash'>('powershell');
  const cmd = azureExportCommand(domain, shell);
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-line bg-bg/60">
      <div className="flex items-center gap-1 border-b border-line px-2 py-1">
        {(['powershell', 'bash'] as const).map((sh) => (
          <button key={sh} onClick={() => setShell(sh)} className={cn('rounded-md px-2 py-0.5 text-[11px] font-medium', shell === sh ? 'bg-raised text-fg shadow-sm' : 'text-muted')}>
            {sh === 'powershell' ? 'PowerShell' : 'Bash'}
          </button>
        ))}
        <span className="flex-1 text-right text-[10.5px] text-faint">Cloud Shell shows which one at the top left</span>
        <IconButton size="sm" label="Copy command" onClick={() => (navigator.clipboard.writeText(cmd), toast.success('Command copied — paste it into Cloud Shell'))}><Copy /></IconButton>
      </div>
      <pre className="selectable max-h-28 overflow-auto whitespace-pre-wrap break-all p-2.5 font-mono text-[11px] leading-relaxed text-fg/85">{cmd}</pre>
    </div>
  );
}

/** What stops working if a record is left behind (shown on excluded rows and in the final confirmation). */
function exclusionImpact(r: MigrationRecord, domain: string): string {
  const host = rel(r.name, domain) === '@' ? domain : r.name;
  if (r.liveStatus === 'missing') return 'Not answered by your current DNS either — probably stale, safe to leave out.';
  switch (r.category) {
    case 'web': return `${host} will stop resolving after the switch — visitors can't reach it.`;
    case 'api': return `${host} will stop resolving — apps calling this API will fail.`;
    case 'verification': return 'A service that verified your domain with this record (e.g. Azure, Google, Microsoft) may stop trusting it or fail certificate renewal.';
    case 'email': return r.emailAction === 'replace' ? 'Replaced by DTS Mail during the email step anyway.' : 'Email signing, delivery or client setup that depends on this record may break.';
    case 'delegation': return `The ${host} subdomain (hosted elsewhere) will stop resolving.`;
    default: return 'Anything that looks up this record will stop finding it after the switch.';
  }
}

const KIND_LABEL: Record<string, string> = { web: 'Websites', api: 'APIs', verification: 'Verification', email: 'Email', delegation: 'Subdomains', other: 'Other' };

function StepRecords({ m, busy, run, onNext }: { m: Migration; busy: string | null; run: Run; onNext: () => void }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ b64: string; type: string; name: string } | null>(null);
  const [over, setOver] = useState(false);
  const [rows, setRows] = useState<MigrationRecord[]>(m.records);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const help = PROVIDERS[m.provider] ?? PROVIDERS.other;
  const locked = !!m.delegatedUtc;
  useEffect(() => setRows(m.records), [m.records]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(m.records);

  // Selection changes are saved automatically (debounced) so "Continue" never loses them.
  const qc = useQueryClient();
  useEffect(() => {
    if (!dirty || locked) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const next = await api.updateMigrationRecords(m.domain, rows);
        qc.setQueryData(['migration', next.domain], next);
      } catch (e) {
        toast.error(`Couldn't save your selection: ${errorMessage(e)}`);
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [rows, dirty, locked, m.domain, qc]);

  const readFile = async (f: File) => {
    if (f.type.startsWith('image/')) {
      const b64 = await new Promise<string>((res) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1] ?? '');
        r.readAsDataURL(f);
      });
      setImage({ b64, type: f.type, name: f.name });
    } else setText(await f.text());
  };

  const importNow = async () => {
    if (m.records.length && !(await confirmDialog('Re-import records?', 'This replaces the current list, including which records you chose not to migrate.', { confirmLabel: 'Re-import' }))) return;
    await run('import', () => api.importMigrationRecords(m.domain, image ? { imageBase64: image.b64, imageType: image.type, fileName: image.name, text: text || undefined } : { text, fileName: 'pasted' }))
      .then((r) => r && (setText(''), setImage(null)));
  };

  const s = search.trim().toLowerCase();
  const matches = (r: MigrationRecord) =>
    (filter === 'all' || r.category === filter || (filter === 'excluded' && !r.include) || (filter === 'issues' && (r.liveStatus !== 'live' || r.source === 'discovered'))) &&
    (!s || r.name.includes(s) || r.value.toLowerCase().includes(s) || r.type.toLowerCase() === s);
  const shown = rows.filter(matches);
  const included = rows.filter((r) => r.include);
  const excluded = rows.filter((r) => !r.include);
  const counts = useMemo(() => rows.reduce<Record<string, number>>((a, r) => ((a[r.category] = (a[r.category] ?? 0) + 1), a), {}), [rows]);
  const allShownIncluded = shown.length > 0 && shown.every((r) => r.include);
  const setInclude = (pred: (r: MigrationRecord) => boolean, include: boolean) => setRows(rows.map((r) => (pred(r) ? { ...r, include } : r)));

  return (
    <div className="space-y-4">
      <InspectSummary m={m} />
      {!locked && (
        <Card>
          <div className="flex items-start gap-6">
            <div className="w-[300px] shrink-0">
              <div className="text-[14px] font-semibold">Export your DNS from {help.name}</div>
              <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-[12.5px] text-muted">
                {help.steps.map((st, i) => <li key={i} className="selectable">{st}</li>)}
              </ol>
              {m.provider === 'azure' && <AzureExportCommand domain={m.domain} />}
              <p className="mt-3 text-[11.5px] text-faint">Any format works: a zone file is read exactly; tables, CSV or screenshots are read by AI and every record is checked against your live DNS.</p>
            </div>
            <div
              className={cn('flex-1 rounded-2xl border-2 border-dashed p-3 transition', over ? 'border-accent bg-accent/10' : 'border-line-strong')}
              onDragOver={(e) => (e.preventDefault(), setOver(true))}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer.files[0];
                if (f) void readFile(f);
              }}
            >
              {image ? (
                <div className="flex items-center gap-3 rounded-xl bg-bg/40 p-3">
                  <ImageIcon className="size-5 text-accent" />
                  <span className="flex-1 truncate text-[13px]">{image.name}</span>
                  <IconButton size="sm" label="Remove" onClick={() => setImage(null)}><X /></IconButton>
                </div>
              ) : (
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={m.records.length ? 4 : 9} placeholder={'Paste the exported zone file or records here…\n\nor drop a file / screenshot onto this box'} className="border-0 bg-transparent font-mono text-[12px] focus:ring-0" />
              )}
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" variant="ghost" icon={<FileUp className="size-3.5" />} onClick={() => fileInput.current?.click()}>Choose file</Button>
                <input ref={fileInput} type="file" hidden accept=".txt,.zone,.db,.csv,.bind,text/*,image/*" onChange={(e) => e.target.files?.[0] && void readFile(e.target.files[0])} />
                <span className="flex-1" />
                <Button variant="primary" loading={busy === 'import'} disabled={!text.trim() && !image} icon={<Sparkles className="size-4" />} onClick={() => void importNow()}>
                  {m.records.length ? 'Re-import' : 'Read records'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          {/* summary */}
          <div className="mb-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-[15px] font-semibold">Choose what to migrate</div>
              <div className="text-[12.5px] text-muted">
                {locked
                  ? 'The nameservers have been switched, so this list is now read-only. Manage records in Cloudflare from here on.'
                  : 'Untick anything you don\'t want in Cloudflare. Records you leave out won\'t exist after the nameservers switch.'}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[12.5px]">
              <span className="rounded-full bg-ok/15 px-3 py-1 font-semibold text-ok">{included.length} will migrate</span>
              <button onClick={() => setFilter(filter === 'excluded' ? 'all' : 'excluded')} className={cn('rounded-full px-3 py-1 font-semibold transition', excluded.length ? 'bg-warn/15 text-warn hover:bg-warn/25' : 'bg-line text-muted')}>
                {excluded.length} won't
              </button>
              {saving ? <span className="flex items-center gap-1 text-faint"><Loader2 className="size-3 animate-spin" />Saving</span> : !dirty && <span className="flex items-center gap-1 text-faint"><Check className="size-3" />Saved</span>}
            </div>
          </div>

          {m.warnings.map((w, i) => (
            <div key={i} className="mb-2 flex gap-2 rounded-xl bg-warn/10 px-3 py-2 text-[12.5px] text-warn"><CircleAlert className="mt-0.5 size-4 shrink-0" />{w}</div>
          ))}

          {/* filters */}
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <div className="relative mr-1">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or value" className="h-8 w-52 pl-8 text-[12.5px]" />
            </div>
            <Chip active={filter === 'all'} onClick={() => setFilter('all')}>All · {rows.length}</Chip>
            {Object.entries(counts).map(([k, n]) => <Chip key={k} active={filter === k} onClick={() => setFilter(k)}>{KIND_LABEL[k] ?? k} · {n}</Chip>)}
            <Chip active={filter === 'excluded'} onClick={() => setFilter('excluded')}>Not migrating · {excluded.length}</Chip>
            <Chip active={filter === 'issues'} onClick={() => setFilter('issues')}>Needs a look</Chip>
            {!locked && shown.length > 0 && (
              <span className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setInclude(matches, true)}>Include {filter === 'all' && !s ? 'all' : 'shown'}</Button>
                <Button size="sm" variant="ghost" onClick={() => setInclude(matches, false)}>Exclude {filter === 'all' && !s ? 'all' : 'shown'}</Button>
              </span>
            )}
          </div>

          {/* table */}
          <div className="max-h-[480px] overflow-auto rounded-xl border border-line">
            <table className="w-full text-left text-[12.5px]">
              <thead className="sticky top-0 z-10 bg-raised text-[11px] uppercase tracking-wider text-faint">
                <tr>
                  <th className="w-9 px-2 py-2">
                    <input type="checkbox" disabled={locked || shown.length === 0} checked={allShownIncluded} onChange={(e) => setInclude(matches, e.target.checked)} title="Include / exclude all shown" />
                  </th>
                  <th className="px-2 py-2">Type</th><th className="px-2 py-2">Name</th><th className="px-2 py-2">Value</th><th className="px-2 py-2">TTL</th><th className="px-2 py-2">Kind</th><th className="px-2 py-2">Live check</th><th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {shown.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-[12.5px] text-muted">{filter === 'excluded' ? 'Every record will be migrated.' : 'No records match.'}</td></tr>
                )}
                {shown.map((r) => {
                  const i = rows.indexOf(r);
                  const set = (patch: Partial<MigrationRecord>) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  const risky = !r.include && r.liveStatus !== 'missing' && r.emailAction !== 'replace';
                  return (
                    <tr key={i} className={cn('transition-colors', !r.include && 'bg-bg/40')}>
                      <td className="px-2 py-1.5 align-top">
                        <input type="checkbox" disabled={locked} checked={r.include} onChange={(e) => set({ include: e.target.checked })} title={r.include ? 'Will be migrated — untick to leave it out' : 'Not migrating — tick to include'} />
                      </td>
                      <td className={cn('px-2 py-1.5 align-top font-mono font-semibold', !r.include && 'text-faint line-through')}>{r.type}</td>
                      <td className={cn('max-w-[180px] truncate px-2 py-1.5 align-top font-mono', !r.include && 'text-faint line-through')} title={r.name}>{rel(r.name, m.domain)}</td>
                      <td className="max-w-[320px] px-2 py-1.5 align-top font-mono" title={r.value}>
                        <div className={cn('selectable truncate', !r.include && 'text-faint line-through')}>{r.priority != null && <span className="text-faint">{r.priority} </span>}{r.value}</div>
                        {r.include && r.note && <div className="truncate font-sans text-[11px] text-faint">{r.note}</div>}
                        {!r.include && (
                          <div className={cn('flex items-start gap-1 whitespace-normal font-sans text-[11px]', risky ? 'text-warn' : 'text-faint')}>
                            {risky && <CircleAlert className="mt-px size-3 shrink-0" />}
                            {exclusionImpact(r, m.domain)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top tabular-nums text-muted">{r.ttl}</td>
                      <td className="px-2 py-1.5 align-top">
                        <span className="pill badge rounded-full px-2 py-px text-[10.5px] font-semibold" style={{ background: tint(CATEGORY_COLORS[r.category] ?? '#64748b'), color: CATEGORY_COLORS[r.category] }}>{KIND_LABEL[r.category] ?? r.category}</span>
                        {r.emailAction === 'replace' && <div className="mt-0.5 text-[10.5px] font-semibold text-danger">→ DTS Mail in email step</div>}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {r.source === 'discovered' ? <span className="text-[11px] font-semibold text-accent">found live, not in export</span>
                          : r.liveStatus === 'live' ? <span className="text-[11px] text-ok">✓ live</span>
                          : r.liveStatus === 'mismatch' ? <span className="text-[11px] text-warn" title={r.liveValue}>differs live</span>
                          : r.liveStatus === 'missing' ? <span className="text-[11px] text-faint">not live</span> : <span className="text-[11px] text-faint">—</span>}
                      </td>
                      <td className="align-top">{!locked && <IconButton size="sm" label="Delete row" onClick={() => setRows(rows.filter((_, j) => j !== i))}><X /></IconButton>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!locked && <AddRecord domain={m.domain} onAdd={(r) => setRows([...rows, r])} />}
          {!locked && (
            <div className="mt-4 flex items-center gap-2">
              <p className="flex-1 text-[12px] text-muted">Have hostnames that aren't listed? Add them now — anything not on this list won't exist after the switch.</p>
              <Button variant="primary" disabled={included.length === 0 || saving || busy === 'save'} icon={<ArrowRight className="size-4" />} onClick={async () => {
                if (dirty) {
                  const next = await run('save', () => api.updateMigrationRecords(m.domain, rows));
                  if (!next) return;
                }
                onNext();
              }}>
                Continue with {included.length} record{included.length === 1 ? '' : 's'}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function AddRecord({ domain, onAdd }: { domain: string; onAdd: (r: MigrationRecord) => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: 'A', name: '', value: '', ttl: '3600', priority: '' });
  if (!open) return <Button size="sm" variant="ghost" className="mt-2" icon={<Plus className="size-3.5" />} onClick={() => setOpen(true)}>Add a record</Button>;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-bg/40 p-2">
      <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="h-9 rounded-xl border border-line bg-raised px-2 text-[13px]">
        {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA', 'NS'].map((t) => <option key={t}>{t}</option>)}
      </select>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="name (@ for root)" className="w-40" />
      {(f.type === 'MX' || f.type === 'SRV') && <Input value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} placeholder="priority" className="w-24" />}
      <Input value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder="value" className="min-w-[220px] flex-1" />
      <Input value={f.ttl} onChange={(e) => setF({ ...f, ttl: e.target.value })} placeholder="ttl" className="w-20" />
      <Button size="sm" variant="primary" disabled={!f.value.trim()} onClick={() => {
        const name = !f.name || f.name === '@' ? domain : f.name.endsWith(domain) ? f.name : `${f.name}.${domain}`;
        onAdd({ name: name.toLowerCase(), type: f.type, value: f.value.trim(), ttl: Number(f.ttl) || 3600, priority: f.priority ? Number(f.priority) : undefined, include: true, source: 'export', category: 'other', liveStatus: 'unknown' });
        setF({ type: 'A', name: '', value: '', ttl: '3600', priority: '' });
        setOpen(false);
      }}>Add</Button>
      <IconButton size="sm" label="Cancel" onClick={() => setOpen(false)}><X /></IconButton>
    </div>
  );
}

// ---------- step 3: copy & verify ----------

function StepCopy({ m, busy, run, onNext }: { m: Migration; busy: string | null; run: Run; onNext: () => void }) {
  const copied = m.status !== 'records' && m.cloudflareNameservers.length > 0;
  const v = m.verify;
  const included = m.records.filter((r) => r.include);
  const excluded = m.records.filter((r) => !r.include);
  const risky = excluded.filter((r) => r.liveStatus !== 'missing' && r.emailAction !== 'replace');
  const copy = async () => {
    if (risky.length > 0) {
      const names = risky.slice(0, 6).map((r) => `${r.type} ${rel(r.name, m.domain)}`).join(', ');
      const ok = await confirmDialog(
        `Leave out ${risky.length} live record${risky.length === 1 ? '' : 's'}?`,
        `These answer on your current DNS today and won't exist after the switch: ${names}${risky.length > 6 ? ', …' : ''}.`,
        { confirmLabel: 'Copy without them', danger: true },
      );
      if (!ok) return;
    }
    await run('copy', async () => { await api.copyMigration(m.domain); return api.verifyMigration(m.domain); });
  };
  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 gap-3">
          <details className="rounded-xl bg-ok/[0.07] px-4 py-3" open={included.length <= 12}>
            <summary className="cursor-pointer text-[13px] font-semibold text-ok">{included.length} record{included.length === 1 ? '' : 's'} will be copied</summary>
            <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto font-mono text-[11.5px] text-fg/80">
              {included.map((r, i) => <li key={i} className="truncate" title={r.value}>{r.type.padEnd(5, '\u00a0')} {rel(r.name, m.domain)} <span className="text-faint">→ {r.value}</span></li>)}
            </ul>
          </details>
          <details className={cn('rounded-xl px-4 py-3', excluded.length ? 'bg-warn/[0.08]' : 'bg-bg/40')} open={excluded.length > 0 && excluded.length <= 12}>
            <summary className={cn('cursor-pointer text-[13px] font-semibold', excluded.length ? 'text-warn' : 'text-muted')}>
              {excluded.length ? `${excluded.length} record${excluded.length === 1 ? '' : 's'} won't be migrated` : 'Nothing is being left out'}
            </summary>
            {excluded.length > 0 && (
              <ul className="mt-2 max-h-48 space-y-1.5 overflow-y-auto text-[11.5px]">
                {excluded.map((r, i) => (
                  <li key={i}>
                    <div className="truncate font-mono text-fg/80">{r.type} {rel(r.name, m.domain)} <span className="text-faint">{r.value}</span></div>
                    <div className="text-muted">{exclusionImpact(r, m.domain)}</div>
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
        <p className="mt-2 text-[11.5px] text-faint">Change the selection on the Records step at any time before the nameservers switch; copying again makes Cloudflare match it exactly.</p>
      </Card>
      <Card>
        <div className="flex items-center gap-4">
          <div className="flex size-11 items-center justify-center rounded-xl bg-accent/15 text-accent"><CloudUpload className="size-5" /></div>
          <div className="flex-1">
            <div className="text-[14px] font-semibold">{copied ? 'Records are in Cloudflare' : `Copy ${m.records.filter((r) => r.include).length} records to Cloudflare`}</div>
            <div className="text-[12.5px] text-muted">Copied exactly, all DNS-only (not proxied) so certificates and host validations keep working. Your current DNS is not touched.</div>
          </div>
          <Button variant={copied ? 'soft' : 'primary'} loading={busy === 'copy'} icon={copied ? <RefreshCw className="size-4" /> : <CloudUpload className="size-4" />}
            onClick={() => void copy()}>
            {copied ? 'Copy again' : 'Copy & verify'}
          </Button>
        </div>
      </Card>

      {copied && (
        <Card className={cn(v?.ready && 'border-ok/40')}>
          <div className="mb-3 flex items-center gap-3">
            {v?.ready ? <ShieldCheck className="size-6 text-ok" /> : <CircleAlert className="size-6 text-warn" />}
            <div className="flex-1">
              <div className="text-[14px] font-semibold">{!v ? 'Not verified yet' : v.ready ? `All ${v.total} record sets match — safe to switch` : `${v.matched} of ${v.total} record sets match`}</div>
              <div className="text-[12.5px] text-muted">We ask your current nameservers and Cloudflare's nameservers the same questions and compare every answer.</div>
            </div>
            <Button variant="soft" loading={busy === 'verify'} icon={<RefreshCw className="size-4" />} onClick={() => run('verify', () => api.verifyMigration(m.domain))}>Verify again</Button>
          </div>
          {v && (
            <div className="max-h-[360px] overflow-auto rounded-xl border border-line">
              <table className="w-full text-left text-[12px]">
                <thead className="sticky top-0 bg-raised text-[11px] uppercase tracking-wider text-faint"><tr><th className="px-2 py-2" /><th className="px-2 py-2">Type</th><th className="px-2 py-2">Name</th><th className="px-2 py-2">Current DNS answers</th><th className="px-2 py-2">Cloudflare answers</th></tr></thead>
                <tbody className="divide-y divide-line">
                  {v.groups.map((g, i) => (
                    <tr key={i} className={cn(!g.match && 'bg-danger/5')}>
                      <td className="px-2 py-1.5">{g.match ? <Check className="size-4 text-ok" /> : <X className="size-4 text-danger" />}</td>
                      <td className="px-2 py-1.5 font-mono font-semibold">{g.type}</td>
                      <td className="px-2 py-1.5 font-mono">{rel(g.name, m.domain)}</td>
                      <td className="selectable max-w-[260px] truncate px-2 py-1.5 font-mono text-muted" title={g.current.join('\n')}>{g.current.join(', ') || '—'}</td>
                      <td className="selectable max-w-[260px] truncate px-2 py-1.5 font-mono" title={g.cloudflare.join('\n')}>{g.cloudflare.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {v && !v.ready && <p className="mt-3 text-[12.5px] text-muted">Cloudflare can take a minute to serve new records — verify again. If a row still differs, go back to Records, fix it, and copy again.</p>}
          <div className="mt-4 flex justify-end">
            <Button variant="primary" disabled={!v?.ready} icon={<ArrowRight className="size-4" />} onClick={onNext}>Continue to nameservers</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- step 4: nameservers ----------

function StepNameservers({ m, busy, run, onNext }: { m: Migration; busy: string | null; run: Run; onNext: () => void }) {
  const delegated = !!m.delegatedUtc;
  useEffect(() => {
    if (delegated) return;
    const t = setInterval(() => void api.checkDelegation(m.domain).then(() => undefined).catch(() => undefined), 45_000);
    return () => clearInterval(t);
  }, [delegated, m.domain]);
  const godaddy = (m.registrar ?? '').toLowerCase().includes('godaddy');
  return (
    <div className="space-y-4">
      <Card className={cn(delegated && 'border-ok/40')}>
        {delegated ? (
          <div className="flex items-center gap-4">
            <CheckCircle2 className="size-8 text-ok" />
            <div className="flex-1">
              <div className="text-[15px] font-semibold">{m.domain} is now served by Cloudflare</div>
              <div className="text-[12.5px] text-muted">Detected {ago(m.delegatedUtc!)}. Your websites and APIs answer exactly as before.</div>
            </div>
            <Button variant="primary" icon={<ArrowRight className="size-4" />} onClick={onNext}>Switch email</Button>
          </div>
        ) : (
          <>
            <div className="text-[15px] font-semibold">Change the nameservers at {m.registrar ?? 'your registrar'}</div>
            <p className="mt-1 text-[12.5px] text-muted">Replace the current nameservers with these two. This is the only step done outside DTS Mail.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {m.cloudflareNameservers.map((ns) => (
                <button key={ns} onClick={() => (navigator.clipboard.writeText(ns), toast.success(`Copied ${ns}`))} className="group flex items-center gap-3 rounded-xl border border-line-strong bg-raised px-4 py-3 text-left transition hover:border-accent/50">
                  <span className="selectable flex-1 font-mono text-[15px] font-semibold">{ns}</span>
                  <ClipboardCopy className="size-4 text-muted group-hover:text-accent" />
                </button>
              ))}
            </div>
            <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-[12.5px] text-muted">
              {godaddy ? (
                <>
                  <li>Sign in to GoDaddy → <b className="text-fg">My Products</b> → <b className="text-fg">Domains</b> → select {m.domain}.</li>
                  <li>Open <b className="text-fg">DNS</b> → <b className="text-fg">Nameservers</b> → <b className="text-fg">Change Nameservers</b>.</li>
                  <li>Choose <b className="text-fg">I'll use my own nameservers</b>, enter the two nameservers above, and save.</li>
                </>
              ) : (
                <>
                  <li>Sign in where {m.domain} is registered ({m.registrar ?? 'your registrar'}).</li>
                  <li>Find the domain's <b className="text-fg">Nameservers</b> setting and choose custom nameservers.</li>
                  <li>Replace all existing nameservers with the two above and save.</li>
                </>
              )}
            </ol>
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-bg/40 px-4 py-3">
              <Loader2 className="size-4 animate-spin text-accent" />
              <span className="flex-1 text-[12.5px] text-muted">Watching for the change — usually minutes, occasionally up to 48 hours. You can close this; you'll get a notification.</span>
              <Button size="sm" variant="soft" loading={busy === 'deleg'} onClick={() => run('deleg', () => api.checkDelegation(m.domain))}>Check now</Button>
            </div>
          </>
        )}
      </Card>
      <Card>
        <div className="flex gap-3 text-[12.5px] text-muted">
          <Undo2 className="mt-0.5 size-4 shrink-0" />
          <div>
            <b className="text-fg">Instant undo:</b> your previous DNS ({m.oldNameservers.join(', ')}) is untouched and keeps answering identically. To roll back, put those nameservers back at your registrar. Keep the old DNS zone for at least two weeks after the move.
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------- step 5: email ----------

function StepEmail({ m, busy, run, onNext }: { m: Migration; busy: string | null; run: Run; onNext: () => void }) {
  const switched = !!m.emailSwitchedUtc;
  const replacing = m.records.filter((r) => r.emailAction === 'replace' && r.include);
  const keeping = m.records.filter((r) => r.category === 'email' && r.emailAction === 'keep' && r.include);
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-4">
          <div className="flex size-11 items-center justify-center rounded-xl ai-gradient text-white"><Mail className="size-5" /></div>
          <div className="flex-1">
            <div className="text-[15px] font-semibold">{switched ? `Email for ${m.domain} goes to DTS Mail` : `Move ${m.domain} email to DTS Mail`}</div>
            <div className="text-[12.5px] text-muted">
              {m.mailProvider === 'none' ? 'The domain receives no email today.' : `Today email goes to ${MAIL_NAMES[m.mailProvider] ?? m.mailProvider}. After the switch new mail arrives in DTS Mail; mail already in ${MAIL_NAMES[m.mailProvider] ?? 'the old mailbox'} stays there.`}
            </div>
          </div>
        </div>
        {!switched && (
          <div className="mt-4 grid grid-cols-2 gap-3 text-[12.5px]">
            <div className="rounded-xl bg-bg/40 p-3">
              <div className="mb-1.5 font-semibold text-fg">Changes</div>
              <ul className="space-y-1 text-muted">
                {replacing.map((r, i) => <li key={i}>• {r.type} {rel(r.name, m.domain)} <span className="font-mono">{r.value}</span> → Cloudflare Email Routing</li>)}
                <li>• Mail delivered to DTS Mail, catch-all to you (no address bounces)</li>
                <li>• Sending enabled through Cloudflare, plus a monitor-only DMARC record if missing</li>
              </ul>
            </div>
            <div className="rounded-xl bg-bg/40 p-3">
              <div className="mb-1.5 font-semibold text-fg">Kept as is</div>
              <ul className="space-y-1 text-muted">
                {keeping.length === 0 ? <li>• Nothing else email-related</li> : keeping.slice(0, 8).map((r, i) => <li key={i}>• {r.type} {rel(r.name, m.domain)}</li>)}
                <li>• Other senders in SPF (app notifications etc.)</li>
              </ul>
            </div>
          </div>
        )}
        {busy === 'email' && (
          <div className="ai-ring busy mt-4 flex items-center gap-3 rounded-xl bg-bg/40 px-4 py-3">
            <Loader2 className="size-4 animate-spin text-accent" />
            <span className="shimmer-text text-[13px] font-medium">Switching records, then sending a test email through Cloudflare and waiting for it to arrive (up to 2½ minutes)…</span>
          </div>
        )}
        {m.emailTest && (
          <div className={cn('mt-4 flex gap-2 rounded-xl px-3 py-2.5 text-[12.5px]', m.emailTestPassed ? 'bg-ok/10 text-ok' : 'bg-warn/10 text-warn')}>
            {m.emailTestPassed ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" /> : <CircleAlert className="mt-0.5 size-4 shrink-0" />}
            {m.emailTest}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          {switched && <Button variant="ghost" icon={<RotateCcw className="size-4" />} loading={busy === 'rollback'} onClick={async () => {
            if (await confirmDialog('Restore the previous email records?', `Mail for ${m.domain} goes back to ${MAIL_NAMES[m.mailProvider] ?? 'the previous provider'}.`, { confirmLabel: 'Restore', danger: true }))
              await run('rollback', () => api.rollbackMigrationEmail(m.domain), 'Email records restored');
          }}>Undo email switch</Button>}
          {!switched || !m.emailTestPassed ? (
            <Button variant="ai" loading={busy === 'email'} disabled={!m.delegatedUtc} icon={<Workflow className="size-4" />} onClick={() => run('email', () => api.switchMigrationEmail(m.domain))}>
              {switched ? 'Run the switch again' : 'Switch email now'}
            </Button>
          ) : (
            <Button variant="primary" icon={<ArrowRight className="size-4" />} onClick={onNext}>Monitoring</Button>
          )}
        </div>
      </Card>
    </div>
  );
}

// ---------- step 6: monitor ----------

function StepMonitor({ m, busy, run }: { m: Migration; busy: string | null; run: Run }) {
  const last = m.monitor[m.monitor.length - 1];
  const hosts = last?.hosts ?? m.baseline;
  return (
    <div className="space-y-4">
      <Card className={cn(m.status === 'done' && 'border-ok/40')}>
        <div className="flex items-center gap-4">
          <HeartPulse className={cn('size-7', !last || last.healthy ? 'text-ok' : 'text-danger')} />
          <div className="flex-1">
            <div className="text-[15px] font-semibold">{m.status === 'done' ? 'Migration complete' : 'Watching your sites for 72 hours'}</div>
            <div className="text-[12.5px] text-muted">Every 15 minutes we check each hostname's DNS, HTTPS and certificate against how it behaved before the move, and notify you of any change.{last && ` Last check ${ago(last.at)}.`}</div>
          </div>
          <Button variant="soft" loading={busy === 'health'} icon={<RefreshCw className="size-4" />} onClick={() => run('health', () => api.migrationHealth(m.domain))}>Check now</Button>
        </div>
        {hosts.length > 0 && (
          <div className="mt-4 divide-y divide-line overflow-hidden rounded-xl border border-line">
            {hosts.map((h) => {
              const ok = h.dnsOk && (h.httpStatus ?? 0) > 0 && (h.httpStatus ?? 0) < 500 && h.certOk !== false;
              return (
                <div key={h.host} className="flex items-center gap-3 px-3 py-2 text-[12.5px]">
                  {ok ? <Check className="size-4 text-ok" /> : <CircleAlert className="size-4 text-warn" />}
                  <span className="flex-1 font-mono">{h.host}</span>
                  <span className="text-muted">{h.error ?? `HTTP ${h.httpStatus}`}</span>
                  {h.certOk && <span className="text-[11px] text-ok">TLS ✓</span>}
                </div>
              );
            })}
          </div>
        )}
        {m.status === 'done' && <p className="mt-3 text-[12.5px] text-muted">You can now delete the old DNS zone at {PROVIDERS[m.provider]?.name ?? 'your previous provider'} whenever you like.</p>}
      </Card>
    </div>
  );
}
